import api from './api'

/**
 * Normalize route data from API response to frontend format
 * Transforms snake_case to camelCase and extracts GeoJSON coordinates
 */
const normalizeRoute = (route) => {
    return {
        id: route._id,
        name: route.name,
        description: route.description,
        stateCode: route.state_code,
        difficulty: route.difficulty,
        distance: route.distance,
        startPoint: route.start_point,
        endPoint: route.end_point,
        // Extract coordinates array from GeoJSON LineString
        coordinates: route.coordinates?.coordinates || [],
        creator: route.creator_id ? {
            id: route.creator_id._id,
            name: route.creator_id.name,
            username: route.creator_id.username,
            avatar: route.creator_id.avatar_url,
        } : null,
        status: route.status,
        rejectionReason: route.rejection_reason,
        source: route.source,
        createdAt: route.created_at,
        updatedAt: route.updated_at,
    }
}

export const routesAPI = {
    /**
     * Fetch public approved routes with optional filters
     * @param {Object} params - Query parameters
     * @param {string} params.state - Filter by state code (e.g., "MH", "CA")
     * @param {string} params.difficulty - Filter by difficulty ("Easy", "Medium", "Hard")
     * @param {string} params.sort - Sort field ("distance", "name", "difficulty", "created_at")
     * @param {string} params.order - Sort order ("asc", "desc")
     * @param {string} params.search - Full-text search query
     * @returns {Promise<Object>} Normalized routes data
     */
    getPublicRoutes: async (params = {}) => {
        const response = await api.get('/routes', { params })

        // Normalize the routes data
        return {
            success: response.success,
            count: response.count,
            routes: response.data.routes.map(normalizeRoute),
        }
    },

    /**
     * Get route by ID
     * @param {string} id - Route ID
     * @returns {Promise<Object>} Route data
     */
    getRouteById: async (id) => {
        const response = await api.get(`/routes/${id}`)
        const route = response.data.route
        return normalizeRoute(route)
    },

    /**
     * Create a new route (requires authentication)
     * @param {FormData} formData - FormData containing gpxFile, name, description, state_code, difficulty, start_point, end_point
     * @returns {Promise<Object>} Created route data
     */
    createRoute: async (formData) => {
        const response = await api.post('/routes', formData, {
            headers: { 'Content-Type': undefined }
        })
        const route = response.data.route
        return {
            success: response.success,
            message: response.message,
            route: normalizeRoute(route),
        }
    },

    /**
     * Get current user's contributed routes
     * @returns {Promise<Array>} User's routes with status information
     */
    getUserRoutes: async () => {
        const response = await api.get('/routes/my-routes')
        return {
            success: response.success,
            routes: response.data.routes.map(normalizeRoute),
        }
    },

    /**
     * Update route metadata (requires authentication and ownership)
     * @param {string} id - Route ID
     * @param {Object} payload - Fields to update (name, description, difficulty, start_point, end_point)
     * @returns {Promise<Object>} Updated route data
     */
    updateRoute: async (id, payload) => {
        const response = await api.put(`/routes/${id}`, payload)
        const route = response.data.route
        return {
            success: response.success,
            message: response.message,
            route: normalizeRoute(route),
        }
    },
}
